import type { Dispatch, RootState } from "@redux/store"
import type { CustomerAccountData } from "@shopify/types"

import Backdrop from "@components/Backdrop"
import Cart from "@components/Cart/hoc/withStore"
import CartToast from "@components/CartToast/hoc/withStore"
import Footer from "@components/Footer/hoc/withContentfulData"
import HappyFox from "@components/HappyFox"
import Header from "@components/Header"
import GlobalDataContext from "@components/Layout/GlobalDataContext"
import MegaMenu from "@components/MegaMenu"
import Menu from "@components/Menu"
import MenuContext from "@components/Menu/MenuContext"
import Search from "@components/Search/hoc/withMatchSorter"
import breakpoints from "@constants/breakpoints"
import { useBaseContext } from "@context/BaseContext"
import useBootstrapClientStorage from "@hooks/useBootstrapClientStorage"
import useCartIdCookieSync from "@hooks/useCartIdCookieSync"
import useElevarEvents from "@hooks/useElevarEvents"
import useElevarUserData from "@hooks/useElevarUserData"
import useHeaderDynamics from "@hooks/useHeaderDynamics"
import useMegaMenuItems from "@hooks/useMegaMenuItems"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import useRenderedPromos from "@hooks/useRenderedPromos"
import useRewardsBalance from "@hooks/useRewardsBalance"
import { useTermlyConsent } from "@hooks/useTermlyConsent"
import { selectCartQuantity, selectCartTotal } from "@redux/selectors"
import { getDecodedShopifyCustomerId } from "@utils/index"
import megaMenuConfig from "@utils/megaMenuConfig"
import get from "lodash-es/get"
import React from "react"
import { connect } from "react-redux"
import styled, { css } from "styled-components"

const getMainStyles = () => {
  const mobileOffset = 56 + 48
  const tabletOffset = 72 + 48
  const desktopOffset = 88 + 36
  return css`
    min-height: calc(100vh - ${mobileOffset}px - 410px);
    margin-top: ${mobileOffset}px;

    @media ${breakpoints.tablet} {
      min-height: calc(100vh - ${tabletOffset}px - 956px);
      margin-top: ${tabletOffset}px;
    }

    @media ${breakpoints.laptop} {
      min-height: calc(100vh - ${desktopOffset}px - 704px);
      margin-top: ${desktopOffset}px;
    }
  `
}

const MegaMenuContainer = styled.div<{ topOffset: number }>`
  ${({ topOffset }) => css`
    z-index: 100;
    position: fixed;
    top: ${topOffset}px;
    left: 0;
  `}
`

const Main = styled.main`
  ${getMainStyles()}
`

const YotpoCustomerContainer = styled.div`
  display: none;
`

declare global {
  interface Window {
    __BASE_CONTEXT__?: BaseContext
  }
}

type BaseContext = {
  filterFunnel: any
  globalData: any
  menuData: any
  updatedAt: string
}

interface LayoutProps {
  authLogout: () => void
  automaticDiscountsFetched: boolean
  cartTotal: null | string
  checkIfMigratedToCartAPI: () => void
  checkoutDataFetched: boolean
  checkoutId: null | string
  children?: React.ReactNode
  countdownTimer?: {
    eventEnd?: string
    eventStart: string
    textAfterEvent?: string
    textBeforeEvent: string
    textDuringEvent?: string
  }
  customerCountryCode: string
  customerData: CustomerAccountData
  customerDataFetched: boolean
  customerLocationFetched: boolean
  data?: any
  fetchAutomaticDiscounts: () => void
  fetchCheckout: (checkoutId: null | string) => void
  fetchCustomerData: ({ callback }: { callback?: (data: any) => void }) => void
  fetchCustomerLocation: () => void
  FixedHeaderComponent?: React.ReactNode
  fixedHeaderTopRef?: any
  location?: any
  migratedToCartAPIChecked: boolean
  productCount: number
  products: any
  searchValue: string
  showFixedHeader?: boolean
}

type MenuConfig = {
  brand?: string
  deviceType?: string
  showShopOnly?: boolean
}

const Layout = (props: LayoutProps) => {
  // BaseContext
  const { data: baseContext } = useBaseContext()
  const filterFunnel = baseContext?.filterFunnel || {}
  const globalData = baseContext?.globalData || { menuItems: [] }
  const menuData = baseContext?.menuData || {}

  // Lifecycle Hooks
  useBootstrapClientStorage({ runOnce: true, search: props.location?.search })
  useCartIdCookieSync(props.checkoutId)

  const {
    enterpriseLink,
    promos,
    promoScrollDelay,
    showPromoBanner,
    supportLink,
  } = globalData
  const renderedPromos = useRenderedPromos(
    promos,
    props.customerCountryCode,
    props.customerLocationFetched
  )

  const hasCountdownTimer = !!props.countdownTimer
  const {
    cartToastYPosition,
    collapsePromoBanner,
    megaMenuOffset,
    shortenDesktopHeader,
  } = useHeaderDynamics({
    collapseThreshold: 50,
    hasCountdownTimer,
    promosCount: renderedPromos.length,
    shortenThreshold: 50,
    showPromoBanner,
  })

  const megaMenuRef = React.useRef<HTMLDivElement | null>(null)
  const megaMenuHeaderRef = React.useRef<HTMLDivElement | null>(null)
  const [activeMenuTab, setActiveMenuTab] = React.useState<string | undefined>(
    undefined
  )

  const megaMenuItems = useMegaMenuItems(
    megaMenuConfig,
    activeMenuTab,
    setActiveMenuTab
  )

  const clearActiveMenuTab = () => {
    setActiveMenuTab(undefined)
  }

  useModalBackgroundClose({
    excludeRefs: [megaMenuHeaderRef],
    isOpen: !!activeMenuTab,
    modalRef: megaMenuRef,
    onRequestClose: clearActiveMenuTab,
  })

  const rewardPoints = useRewardsBalance(props.customerData?.id)

  const { elevarOnUserData } = useElevarEvents()
  useElevarUserData({
    cartTotal: props.cartTotal,
    checkoutDataFetched: props.checkoutDataFetched,
    checkoutId: props.checkoutId,
    customer: {
      email: props.customerData?.emailAddress?.emailAddress,
      id: props.customerData?.id,
    },
    customerDataFetched: props.customerDataFetched,
    elevarOnUserData,
    pathname: props.location?.pathname ?? "/",
    products: props.products || [],
  })

  React.useEffect(() => {
    if (props.customerData?.id) {
      ;(window as any).maestraRewardsExtension = {
        customer: {
          email: props.customerData?.emailAddress?.emailAddress,
          id: props.customerData.id,
          name: props.customerData.firstName,
        },
      }
    }
  }, [props.customerData])

  useTermlyConsent({
    buttonIds: [
      "desktopTermlyButton",
      "tabletTermlyButton",
      "mobileTermlyButton",
    ],
    match: "all",
  })

  React.useEffect(() => {
    props.checkIfMigratedToCartAPI?.()
    props.fetchCustomerLocation?.()
    props.fetchAutomaticDiscounts?.()
  }, [])

  React.useEffect(() => {
    if (
      props.migratedToCartAPIChecked &&
      !props.checkoutDataFetched &&
      props.checkoutId
    ) {
      props.fetchCheckout?.(props.checkoutId)
    }
  }, [
    props.migratedToCartAPIChecked,
    props.checkoutDataFetched,
    props.checkoutId,
  ])

  React.useEffect(() => {
    if ((props.location?.pathname ?? "") === "/auth/callback/") return
    if (!props.customerDataFetched) props.fetchCustomerData?.({})
  }, [props.customerDataFetched])

  // Layout State
  const [cart, setCart] = React.useState(false)
  const [menu, setMenu] = React.useState(false)
  const [menuConfig, setMenuConfig] = React.useState<MenuConfig | undefined>(
    undefined
  )

  const openCartWithoutEvent = React.useCallback(() => {
    setCart(true)
  }, [])

  const closeCartWithoutEvent = React.useCallback(() => {
    setCart(false)
  }, [])

  const onClickCart = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setCart(true)
  }, [])

  const openMenu = React.useCallback(
    (config?: {
      brand?: string
      deviceType?: string
      showShopOnly?: boolean
    }) => {
      setMenu(true)

      if (config) {
        setMenuConfig(config)
      }
    },
    []
  )

  const closeMenu = React.useCallback(() => {
    setMenu(false)
    setMenuConfig(undefined)
  }, [])

  const onClickMenu = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      openMenu()
    },
    [openMenu]
  )

  const [search, setSearch] = React.useState(false)

  const openSearch = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setSearch(true)
  }, [])

  const closeSearch = React.useCallback(() => {
    setSearch(false)
  }, [])

  const onLogout = React.useCallback(() => {
    props.authLogout()
    setMenu(false)
  }, [props.authLogout])

  const isLoggedIn = !!props.customerData?.id
  const filters = get(props, "data.filterData.filters")
  const contentfulProduct = get(props, "data.contentfulProduct")
  const contentfulPrintedDesign = get(props, "data.contentfulPrintedDesign")

  const { countryProhibitedProducts } = get(
    props,
    "data.contentfulGlobalSettings",
    {}
  )

  const automaticDiscountData = globalData.automaticDiscounts || []

  const globalDataValue = React.useMemo(
    () => ({ countryProhibitedProducts, showPromoBanner }),
    [countryProhibitedProducts, showPromoBanner]
  )

  const menuCtx = React.useMemo(() => ({ openMenu }), [openMenu])

  return (
    <>
      <Header
        collapsePromoBanner={collapsePromoBanner}
        countdownTimer={props.countdownTimer}
        enterpriseLink={enterpriseLink}
        filters={filters}
        FixedHeaderComponent={get(props, "FixedHeaderComponent", undefined)}
        fixedHeaderTopRef={get(props, "fixedHeaderTopRef", undefined)}
        isLoggedIn={isLoggedIn}
        megaMenuActiveItem={activeMenuTab}
        megaMenuHeaderItems={megaMenuItems}
        megaMenuHeaderRef={megaMenuHeaderRef}
        onClickCart={onClickCart}
        onClickMenu={onClickMenu}
        onClickSearch={openSearch}
        onLogout={onLogout}
        product={contentfulProduct || contentfulPrintedDesign}
        productCount={get(props, "productCount", 0)}
        promos={renderedPromos}
        promoScrollDelay={promoScrollDelay || "5000"}
        rewardsPoints={rewardPoints}
        searchValue={get(props, "searchValue", "")}
        shortenDesktopHeader={shortenDesktopHeader}
        showFilters={
          contentfulProduct !== undefined ||
          contentfulPrintedDesign !== undefined
        }
        showFixedHeader={get(props, "showFixedHeader", undefined)}
        showPromoBanner={showPromoBanner}
        showRewardsPoints={isLoggedIn}
        supportLink={supportLink}
      />
      <Backdrop isOpen={!!activeMenuTab} topOffset={megaMenuOffset}>
        <MegaMenuContainer ref={megaMenuRef} topOffset={megaMenuOffset}>
          <MegaMenu
            activeItem={activeMenuTab}
            data={menuData}
            megaMenuLinks={globalData.megaMenuLinks || []}
            onRequestClose={clearActiveMenuTab}
            topOffset={megaMenuOffset}
          />
        </MegaMenuContainer>
      </Backdrop>
      <GlobalDataContext.Provider value={globalDataValue}>
        <MenuContext.Provider value={menuCtx}>
          <Main id="mainContent">{props.children}</Main>
        </MenuContext.Provider>
      </GlobalDataContext.Provider>
      <Footer user={props.customerData.firstName || "Log In"} />
      <Menu
        config={menuConfig}
        filterFunnel={filterFunnel}
        globalData={globalData}
        isLoggedIn={isLoggedIn}
        isOpen={menu}
        menuData={menuData}
        onLogout={onLogout}
        onRequestClose={closeMenu}
        rewardsPoints={rewardPoints}
      />
      <Search
        automaticDiscountData={automaticDiscountData}
        isOpen={search}
        onRequestClose={closeSearch}
        searchSuggestions={globalData.searchSuggestions || []}
      />
      <Cart
        automaticDiscountData={automaticDiscountData}
        isOpen={cart}
        onRequestClose={closeCartWithoutEvent}
      />
      <CartToast
        onClickCart={openCartWithoutEvent}
        yPosition={`${cartToastYPosition}px`}
      />
      {props.customerData?.id &&
        props.customerData?.emailAddress?.emailAddress && (
          <YotpoCustomerContainer
            data-authenticated="true"
            data-email={props.customerData.emailAddress?.emailAddress}
            data-id={getDecodedShopifyCustomerId(props.customerData.id)}
            id="swell-customer-identification"
          />
        )}
      <HappyFox />
    </>
  )
}

const mapState = (state: RootState) => ({
  automaticDiscountsFetched: state.discounts.automaticDiscountsFetched,
  cartTotal: selectCartTotal(state),
  checkoutDataFetched: state.checkout.checkoutDataFetched,
  checkoutId: state.checkout.checkoutId,
  customerCountryCode: state.customer.customerLocation.customerCountryCode,
  customerData: state.customer.data,
  customerDataFetched: state.customer.customerDataFetched,
  customerLocationFetched: state.customer.customerLocationFetched,
  migratedToCartAPIChecked: state.checkout.migratedToCartAPIChecked,
  productCount: selectCartQuantity(state),
  products: state.checkout.localCheckout?.items || [],
  searchValue: state.search.searchValue,
})

const mapDispatch = (dispatch: Dispatch) => ({
  authLogout: dispatch.customer.authLogout,
  checkIfMigratedToCartAPI: dispatch.checkout.checkIfMigratedToCartAPI,
  fetchAutomaticDiscounts: dispatch.discounts.fetchAutomaticDiscounts,
  fetchCheckout: dispatch.checkout.fetchCheckout,
  fetchCustomerData: dispatch.customer.fetchCustomerData,
  fetchCustomerLocation: dispatch.customer.fetchCustomerLocation,
})

export default connect(mapState, mapDispatch)(Layout)
